import { clampTo, hasImageInput } from '../../tools';
import type { KieModelConfig } from '../types';

export const googleConfigs: Record<string, KieModelConfig> = {
    'nano-banana-2-lite': {
        // https://docs.kie.ai/cn/market/google/nano-banana-2-lite
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, size, aspectRatio } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'nano-banana-2-lite',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: size === 'match' ? 'auto' : clampTo(
                            aspectRatio || '16:9',
                            ['21:9', '16:9', '9:16', '8:1', '1:8', '5:4', '4:5', '4:1', '1:4', '4:3', '3:4', '3:2', '2:3', '1:1'] as const,
                            '16:9',
                        ),
                        // 图片 URL
                        image_urls: image_urls.slice(0, 10),
                    },
                };
            } else {
                return {
                    model: 'nano-banana-2-lite',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: size === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '8:1', '1:8', '5:4', '4:5', '4:1', '1:4', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                    },
                };
            }
        },
    },
    'nano-banana-2-1': {
        // https://kie.ai/nano-banana-2-1
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, size, aspectRatio, image_resolution, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'nano-banana-2-1',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: size === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '8:1', '1:8', '5:4', '4:5', '4:1', '1:4', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K'),
                        image_input: image_urls.slice(0, 14),
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['jpg', 'png'] as const,
                            'png',
                        ),
                    },
                };
            } else {
                return {
                    model: 'nano-banana-2-1',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '8:1', '1:8', '5:4', '4:5', '4:1', '1:4', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K'),
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['jpg', 'png'] as const,
                            'png',
                        ),
                    },
                };
            }
        },
    },
    'nano-banana-2': {
        // https://docs.kie.ai/cn/market/google/nanobanana2
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, size, aspectRatio, image_resolution, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                // 图生图
                return {
                    model: 'nano-banana-2',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: size === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '8:1', '1:8', '5:4', '4:5', '4:1', '1:4', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K'),
                        // 图片 URL
                        image_input: image_urls.slice(0, 14),
                        // 可选参数
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['jpg', 'png'] as const,
                            'png',
                        ),
                    },
                };
            } else {
                return {
                    model: 'nano-banana-2',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '8:1', '1:8', '5:4', '4:5', '4:1', '1:4', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K'),
                        // 可选参数
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['jpg', 'png'] as const,
                            'png',
                        ),
                    },
                };
            }
        },
    },
    'nano-banana-pro': {
        // https://docs.kie.ai/cn/market/google/pro-image-to-image
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, size, aspectRatio, seed, image_size, image_resolution, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'nano-banana-pro',
                    input: {
                        prompt: prompt.slice(0, 10000),
                        aspect_ratio: size === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                        resolution: clampTo( image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K' ),
                        // 图片 URL
                        image_input: image_urls.slice(0, 8),
                        // 可选参数
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['jpg', 'png'] as const,
                            'png',
                        ),
                    },
                };
            } else {
                return {
                    model: 'nano-banana-pro',
                    input: {
                        prompt: prompt.slice(0, 10000),
                        aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                        resolution: clampTo( image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K' ),
                        // 可选参数
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['jpg', 'png'] as const,
                            'png',
                        ),
                    },
                };
            }
        },
    },
    'nano-banana': {
        // https://docs.kie.ai/cn/market/google/nano-banana
        // https://docs.kie.ai/cn/market/google/nano-banana-edit
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, size, aspectRatio, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'google/nano-banana-edit',
                    input: {
                        prompt: prompt.slice(0, 5000),
                        aspect_ratio: size === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                        // 图片 URL
                        image_urls: image_urls.slice(0, 10),
                        // 可选参数
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['jpeg', 'png'] as const,
                            'png',
                        ),
                    },
                };
            } else {
                return {
                    model: 'google/nano-banana',
                    input: {
                        prompt: prompt.slice(0, 5000),
                        aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                        // 可选参数
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['jpeg', 'png'] as const,
                            'png',
                        ),
                    },
                };
            }
        },
    },
};